import { Transition } from "components/shared/column";
import Loader from "components/shared/loader";
import { catalogEntityOptionFlags, catalogEntityOptionFlagsEnum, entityTypesEnum } from "helpers/enums";
import { buildConfigByProperties, getInitialStateFromConfig } from "helpers/form";
import { flagVal } from "helpers/system";
import { requiredField } from "helpers/validators";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import { getList } from "sources/catalogs";
import { getCombinedProperties } from "sources/extendedProperties";
import FormBody from "./body";
import { config as formConfig } from "./config";
import { customConnect } from "./container";

const hiddenFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Invisible);
const requiredFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Required);

const entityTypesMappers = {
    [entityTypesEnum.DistributionBidCargo]: "cargos",
    [entityTypesEnum.DistributionBidPoint]: "bidPoints",
};

const Form = (props) => {
    const [propsConfigLoaded, setPropsConfigLoaded] = useState(false);
    const [extendedPropertiesConfig, setExtendedPropertiesConfig] = useState([]);
    const [entityOptions, setEntityOptions] = useState([]);
    useEffect(() => {
        Promise.all([
            getCombinedProperties({ isRelation: true }),
            getList([
                entityTypesEnum.TruckingBid,
                entityTypesEnum.Cargo,
                entityTypesEnum.BidPoint,
                entityTypesEnum.DistributionBid,
                entityTypesEnum.DistributionBidCargo,
                entityTypesEnum.DistributionBidPoint,
            ]),
        ]).then(([extendedPropertiesConfig, entityOptions]) => {
            const options = entityOptions.filter(
                (option) =>
                    !option.propertyName ||
                    [
                        entityTypesEnum.DistributionBid,
                        entityTypesEnum.DistributionBidCargo,
                        entityTypesEnum.DistributionBidPoint,
                    ].includes(option.entityType),
            );
            setEntityOptions(options);
            setExtendedPropertiesConfig(extendedPropertiesConfig);
            setPropsConfigLoaded(true);
        });
    }, []);
    if (!propsConfigLoaded) {
        return (
            <Transition dir="full">
                <Loader prefix="absolute-center" />
            </Transition>
        );
    }

    const config = cloneDeep(formConfig);

    config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.bid, { isRelation: true }),
    };
    config.bidPoints.config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.bidPoint, { isRelation: true }),
    };
    config.cargos.config.extendedProperties = {
        type: "extendedProperty",
        config: buildConfigByProperties(extendedPropertiesConfig.cargo, { isRelation: true }),
    };

    entityOptions
        .filter((option) => !!option.propertyName)
        .forEach((option) => {
            const entityConfig = entityTypesMappers[option.entityType]
                ? config[entityTypesMappers[option.entityType]].config
                : config;
            const isRequired = (option.intFlags & requiredFlagVal) === requiredFlagVal;
            const isHidden = (option.intFlags & hiddenFlagVal) === hiddenFlagVal;
            const propertyName = option.propertyName.charAt(0).toLowerCase() + option.propertyName.slice(1);
            if (isRequired && entityConfig[propertyName]) {
                entityConfig[propertyName].required = true;
                entityConfig[propertyName].validators = [
                    ...(entityConfig[propertyName].validators || []),
                    requiredField,
                ];
            }
            if (isHidden && entityConfig[propertyName]) {
                entityConfig[propertyName].isHidden = true;
            }
            if (option.displayName && entityConfig[propertyName]?.inputProps) {
                entityConfig[propertyName].inputProps.label = option.displayName;
            }
        });

    const initialState = getInitialStateFromConfig(config);
    initialState.extendedProperties = getInitialStateFromConfig(config.extendedProperties.config, {
        isExtendedProp: true,
    });
    initialState.bidPoints.forEach((p) => {
        p.extendedProperties = getInitialStateFromConfig(config.bidPoints.config.extendedProperties.config, {
            isExtendedProp: true,
        });
    });
    initialState.cargos.forEach((p) => {
        p.extendedProperties = getInitialStateFromConfig(config.cargos.config.extendedProperties.config, {
            isExtendedProp: true,
        });
    });
    if (props.action === "new") {
        initialState.responsibleId = parseInt(props.profile.id);
    }
    return <FormBody {...props} {...{ config, initialState, entityOptions }} />;
};

export default customConnect(Form, "distributionbids");
